import { CheckCircle2, XCircle } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { SectionHeader } from '@/components/ui/SectionHeader'
import { Skeleton } from '@/components/ui/Skeleton'
import { StatusBadge } from '@/components/ui/StatusBadge'
import { BOLT, fmtDateTime, fmtInt, fmtMoney } from '@/lib/format'

/** GET /accounting/summary → `reconciliation` (the latest nightly check, or null). */
export interface Reconciliation {
  id: number
  checked_at: string
  ok: boolean
  recognized_rub: string
  wallet_value_rub: string
  in_transit_rub: string
  liabilities_rub: string
  cash_in_rub: string
  refunds_rub: string
  conversions_rub: string
  diff_rub: string
  wallets_checked: number
  wallet_mismatches: number
  value_mismatches: number
}

export interface ReconciliationStatusProps {
  /** undefined while loading, null when no check ran yet */
  data: Reconciliation | null | undefined
  canRun?: boolean
  running?: boolean
  onRun?: () => void
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="min-w-0 text-ink-2">{k}</span>
      <span className="tnum shrink-0 text-right text-ink-1">{v}</span>
    </div>
  )
}

/**
 * «Сверка» (docs/accounting.md «Ежедневная сверка»): every night the books are checked —
 * recognized revenue + what is owed for unspent ⚡ must equal what came in − refunds + partner
 * USDT converted to ⚡, and every wallet must match its journal. The state is colour + icon +
 * word (rule 21): «Сверено ✓ дата» or «Расхождение ✕ сумма».
 */
export function ReconciliationStatus({ data, canRun = false, running = false, onRun }: ReconciliationStatusProps) {
  const action = canRun ? (
    <Button size="sm" variant="secondary" loading={running} onClick={onRun}>
      Сверить сейчас
    </Button>
  ) : undefined
  const header = (
    <SectionHeader
      as="h3"
      title="Сверка"
      description="Каждую ночь: выручка + обязательства = поступления − возвраты + переводы, и каждый кошелёк сходится с журналом."
      actions={action}
    />
  )
  if (data === undefined) {
    return (
      <section>
        {header}
        <Card>
          <div role="status" aria-label="Загрузка">
            <Skeleton className="h-5 w-1/2" />
          </div>
        </Card>
      </section>
    )
  }
  if (data === null) {
    return (
      <section>
        {header}
        <Card>
          <StatusBadge tone="neutral">Ещё не сверялось</StatusBadge>
          <p className="mt-2 text-caption text-ink-3">Первая сверка пройдёт ночью в 03:45 по Москве.</p>
        </Card>
      </section>
    )
  }
  const wallets = data.wallet_mismatches + data.value_mismatches
  return (
    <section>
      {header}
      <Card>
        <div className="flex flex-wrap items-center gap-2">
          {data.ok ? (
            <StatusBadge tone="ok" icon={<CheckCircle2 />}>
              Сверено ✓ {fmtDateTime(data.checked_at)}
            </StatusBadge>
          ) : (
            <StatusBadge tone="danger" icon={<XCircle />}>
              Расхождение ✕ {fmtMoney(data.diff_rub, 'RUB')}
            </StatusBadge>
          )}
          {!data.ok && <span className="text-caption text-ink-3">проверка {fmtDateTime(data.checked_at)}</span>}
        </div>
        <div className="mt-3 flex flex-col gap-2 text-body">
          <Row k="Признанная выручка, всё время" v={fmtMoney(data.recognized_rub, 'RUB')} />
          <Row k={`Обязательства за неизрасходованные ${BOLT}`} v={fmtMoney(data.liabilities_rub, 'RUB')} />
          <Row k="Поступления оплат" v={fmtMoney(data.cash_in_rub, 'RUB')} />
          <Row k="Возвраты" v={fmtMoney(data.refunds_rub, 'RUB')} />
          <Row k={`Переводы партнёрских USDT в ${BOLT}`} v={fmtMoney(data.conversions_rub, 'RUB')} />
          <Row k="Разница" v={fmtMoney(data.diff_rub, 'RUB')} />
          <Row
            k="Кошельков проверено"
            v={wallets ? `${fmtInt(data.wallets_checked)}, не сходятся ${fmtInt(wallets)}` : fmtInt(data.wallets_checked)}
          />
        </div>
      </Card>
    </section>
  )
}
